import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SubTitle from '../common/SubTitle';
import RouteSelection from './RouteSelection';
import RouteAddMap from './RouteAddMap';
import RouteAddDetails from './RouteAddDetails';
import { useParams, useLocation } from "react-router-dom";
import config from '../../config.json';

export default function RouteAddMain() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const [orgId, setOrgId] = useState(null);
    const routeToUpdate = location.state?.rowData;
    const path = id || routeToUpdate ? "Routes > Update Routes" : "Routes > Create Routes";
    const isUpdateMode = Boolean(id || routeToUpdate);

    const [routeData, setRouteData] = useState(() => {
        console.log("Initializing routeData with:", routeToUpdate);

        // Get organizationId from localStorage during initial state setup
        let organizationId = null;
        try {
            const userStateString = localStorage.getItem('userState');
            if (userStateString) {
                const userState = JSON.parse(userStateString);
                organizationId = userState.user.organizationId;
                setOrgId(organizationId);
            }
        } catch (error) {
            console.error("Error retrieving organizationId from localStorage:", error);
        }

        if (!routeToUpdate) {
            return {
                routeId: "",
                routeName: "",
                routeType: "BI_DIRECTIONAL",
                routeColor: "",
                origin: null,
                destination: null,
                organizationId: organizationId,
                routePoints: [],
                selectedStops: [],
                selectedVehicles: []
            };
        }

        // Extract route points and stops from existing route
        const routePoints = [];
        const selectedStops = [];

        const stopsMap = {};
        if (routeToUpdate.stops?.length > 0) {
            routeToUpdate.stops.forEach(stop => {
                stopsMap[`${stop.latitude}_${stop.longitude}`] = stop;

                if (routeToUpdate.stops.length > 2 &&
                    routeToUpdate.stops.indexOf(stop) !== 0 &&
                    routeToUpdate.stops.indexOf(stop) !== routeToUpdate.stops.length - 1) {
                    selectedStops.push({
                        id: stop.id,
                        latitude: stop.latitude,
                        longitude: stop.longitude,
                        name: stop.name
                    });
                }
            });
        }

        if (routeToUpdate.routeCoordinates?.length > 0) {
            const midPoints = routeToUpdate.routeCoordinates.length > 2
                ? routeToUpdate.routeCoordinates.slice(1, -1)
                : [];

            midPoints.forEach((point, index) => {
                const coordKey = `${point.latitude}_${point.longitude}`;
                const matchingStop = stopsMap[coordKey];
                const isStop = Boolean(matchingStop);

                const routePoint = {
                    lat: Number(point.latitude),
                    lng: Number(point.longitude),
                    isStop: isStop,
                    stopId: isStop ? matchingStop.id : null,
                    name: isStop ? matchingStop.name : `IM${index + 1}`
                };

                routePoints.push(routePoint);
            });
        }

        let origin = null;
        let destination = null;

        if (routeToUpdate.stops?.length > 0) {
            const firstStop = routeToUpdate.stops[0];
            origin = {
                id: firstStop.id,
                latitude: firstStop.latitude,
                longitude: firstStop.longitude,
                name: firstStop.name
            };

            if (routeToUpdate.stops.length > 1) {
                const lastStop = routeToUpdate.stops[routeToUpdate.stops.length - 1];
                destination = {
                    id: lastStop.id,
                    latitude: lastStop.latitude,
                    longitude: lastStop.longitude,
                    name: lastStop.name
                };
            }
        }

        if (!origin && routeToUpdate.routeCoordinates?.length > 0) {
            const firstPoint = routeToUpdate.routeCoordinates[0];
            origin = {
                id: firstPoint.stopId,
                latitude: firstPoint.latitude,
                longitude: firstPoint.longitude,
                name: firstPoint.name || "Origin"
            };
        }

        if (!destination && routeToUpdate.routeCoordinates?.length > 1) {
            const lastPoint = routeToUpdate.routeCoordinates[routeToUpdate.routeCoordinates.length - 1];
            destination = {
                id: lastPoint.stopId,
                latitude: lastPoint.latitude,
                longitude: lastPoint.longitude,
                name: lastPoint.name || "Destination"
            };
        }

        console.log("Extracted origin:", origin);
        console.log("Extracted destination:", destination);
        console.log("Extracted routePoints:", routePoints);
        console.log("Extracted selectedStops:", selectedStops);

        return {
            routeId: routeToUpdate.routeId,
            routeName: routeToUpdate.name,
            routeType: routeToUpdate.routeType || "BI_DIRECTIONAL",
            routeColor: routeToUpdate.routeColor,
            origin,
            destination,
            organizationId: organizationId || routeToUpdate.organizationId,
            routePoints,
            selectedStops,
            selectedVehicles: routeToUpdate.vehicles || []
        };
    });

    const [allStopsData, setAllStopsData] = useState([]);
    const [allVehiclesData, setAllVehiclesData] = useState([]);

    useEffect(() => {
        fetchStopsData();
        fetchVehiclesData();
    }, []);

    const handleRouteDataChange = (field, value) => {
        setRouteData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleStopSelect = (stop) => {
        const isAlreadySelected = routeData.selectedStops.some(s => s.id === stop.id);

        if (isAlreadySelected) {
            const updatedSelectedStops = routeData.selectedStops.filter(s => s.id !== stop.id);
            const updatedRoutePoints = routeData.routePoints.filter(p => !(p.isStop && p.stopId === stop.id));

            setRouteData(prev => ({
                ...prev,
                selectedStops: updatedSelectedStops,
                routePoints: updatedRoutePoints
            }));
        } else {
            const updatedSelectedStops = [...routeData.selectedStops, stop];
            const newRoutePoint = {
                lat: Number(stop.latitude),
                lng: Number(stop.longitude),
                isStop: true,
                stopId: stop.id,
                name: stop.name
            };

            const updatedRoutePoints = [...routeData.routePoints, newRoutePoint];

            setRouteData(prev => ({
                ...prev,
                selectedStops: updatedSelectedStops,
                routePoints: updatedRoutePoints
            }));
        }
    };

    const fetchStopsData = async () => {
        try {
            const response = await axios.get(`${config.baseURL}/stop/organization`, {
                withCredentials: true
            });
            setAllStopsData(response.data);
        } catch (error) {
            console.error("Error fetching stops:", error);
            if (error.response && error.response.status === 401) {
                navigate("/");
            }
        }
    };

    const fetchVehiclesData = async () => {
        try {
            const response = await axios.get(`${config.baseURL}/vehicle/organization`, {
                withCredentials: true
            });
            console.log(response.data, "naveed test");
            setAllVehiclesData(response.data);
        } catch (error) {
            console.error("Error fetching vehicles:", error);
            if (error.response && error.response.status === 401) {
                navigate("/");
            }
        }
    };

    const handleRouteTypeChange = (type) => {
        setRouteData(prev => ({
            ...prev,
            routeType: type === 'UNI_DIRECTIONAL' ? 'UNI_DIRECTIONAL' : 'BI_DIRECTIONAL'
        }));
    };

    const handleRouteDetailsChange = (field, value) => {
        setRouteData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleStopSelection = (stop) => {
        const isSelected = routeData.selectedStops.some(s => s.id === stop.id);

        let updatedStops, updatedRoutePoints;
        if (isSelected) {
            updatedStops = routeData.selectedStops.filter(s => s.id !== stop.id);
            updatedRoutePoints = routeData.routePoints.filter(
                point => !(point.isStop && point.stopId === stop.id)
            );
        } else {
            updatedStops = [...routeData.selectedStops, stop];
            updatedRoutePoints = [
                ...routeData.routePoints,
                {
                    lat: Number(stop.latitude),
                    lng: Number(stop.longitude),
                    isStop: true,
                    stopId: stop.id,
                    name: stop.name
                }
            ];
        }

        setRouteData(prev => ({
            ...prev,
            selectedStops: updatedStops,
            routePoints: updatedRoutePoints
        }));
    };

    const handleVehicleSelection = (vehicle) => {
        const isSelected = routeData.selectedVehicles.some(v => v.id === vehicle.id);

        const updatedVehicles = isSelected
            ? routeData.selectedVehicles.filter(v => v.id !== vehicle.id)
            : [...routeData.selectedVehicles, vehicle];

        setRouteData(prev => ({
            ...prev,
            selectedVehicles: updatedVehicles
        }));
    };

    const prepareRouteCoordinates = () => {
        const coordinates = [];

        if (routeData.origin) {
            coordinates.push({
                longitude: routeData.origin.longitude,
                latitude: routeData.origin.latitude,
                isStop: true,
                stopId: routeData.origin.id
            });
        }

        if (routeData.routePoints && routeData.routePoints.length > 0) {
            routeData.routePoints.forEach(point => {
                coordinates.push({
                    longitude: point.lng,
                    latitude: point.lat,
                    isStop: point.isStop || false,
                    stopId: point.stopId || null
                });
            });
        }

        if (routeData.destination) {
            coordinates.push({
                longitude: routeData.destination.longitude,
                latitude: routeData.destination.latitude,
                isStop: true,
                stopId: routeData.destination.id
            });
        }

        return coordinates;
    };

    const handleSubmitRoute = async () => {
        try {
            const isUpdate = id || routeToUpdate;
            const routeId = isUpdate ? (id || routeToUpdate.id) : null;

            if (isUpdate) {
                const basicRouteData = {
                    id: routeId,
                    routeId: routeData.routeId,
                    name: routeData.routeName,
                    routeColor: routeData.routeColor,
                    organizationId: orgId
                };

                console.log("Updating basic route data:", basicRouteData);

                const basicUpdateResponse = await axios.put(
                    `${config.baseURL}/route/`,
                    basicRouteData,
                    { withCredentials: true }
                );

                console.log("Route basic info updated successfully:", basicUpdateResponse.data);

                const routeCoordinates = [];

                if (routeData.origin) {
                    routeCoordinates.push({
                        longitude: routeData.origin.longitude,
                        latitude: routeData.origin.latitude
                    });
                }

                if (routeData.routePoints && routeData.routePoints.length > 0) {
                    routeData.routePoints.forEach(point => {
                        routeCoordinates.push({
                            longitude: point.lng,
                            latitude: point.lat
                        });
                    });
                }

                if (routeData.destination) {
                    routeCoordinates.push({
                        longitude: routeData.destination.longitude,
                        latitude: routeData.destination.latitude
                    });
                }

                const coordinatesData = {
                    id: routeId,
                    routeType: routeData.routeType,
                    routeCoordinates: routeCoordinates
                };

                console.log("Updating coordinates data:", coordinatesData);

                const coordinatesUpdateResponse = await axios.patch(
                    `${config.baseURL}/route/coordinates/`,
                    coordinatesData,
                    { withCredentials: true }
                );

                console.log("Route coordinates updated successfully:", coordinatesUpdateResponse.data);

                if (routeData.selectedVehicles && routeData.selectedVehicles.length > 0) {
                    const vehicleAssignmentData = {
                        routeId: routeId,
                        vehicleIds: routeData.selectedVehicles.map(vehicle => vehicle.id)
                    };

                    const vehicleUpdateResponse = await axios.patch(
                        `${config.baseURL}/route/vehicles`,
                        vehicleAssignmentData,
                        { withCredentials: true }
                    );

                    console.log("Vehicles assigned successfully:", vehicleUpdateResponse.data);
                }

                navigate("/route");
            } else {
                const routeCoordinates = prepareRouteCoordinates();

                const formData = {
                    routeId: routeData.routeId,
                    name: routeData.routeName,
                    routeType: routeData.routeType,
                    routeCoordinates: routeCoordinates,
                    routeColor: routeData.routeColor,
                    organizationId: orgId
                };

                console.log("Creating new route:", formData);

                const routeResponse = await axios.post(
                    `${config.baseURL}/route`,
                    formData,
                    { withCredentials: true }
                );

                console.log("Route created successfully:", routeResponse.data);

                if (routeResponse.data && routeResponse.data.id) {
                    const newRouteId = routeResponse.data.id;

                    if (routeData.selectedVehicles && routeData.selectedVehicles.length > 0) {
                        const vehicleAssignmentData = {
                            routeId: newRouteId,
                            vehicleIds: routeData.selectedVehicles.map(vehicle => vehicle.id)
                        };

                        await axios.patch(
                            `${config.baseURL}/route/vehicles`,
                            vehicleAssignmentData,
                            { withCredentials: true }
                        );
                    }

                    navigate("/route");
                }
            }
        } catch (error) {
            console.error("Error submitting route:", error);
        }
    };

    return (
        <>
            <SubTitle title="routes" path={path} />
            <RouteSelection
                routeType={routeData.routeType === 'UNI_DIRECTIONAL' ? 'UNI_DIRECTIONAL' : 'BI_DIRECTIONAL'}
                onRouteTypeChange={handleRouteTypeChange}
                onRouteSchedulesClick={() => {
                    console.log("Route schedules clicked",routeData.routeId);
                    navigate("/route/schedules", {
                        state: {
                            id: routeData.routeId
                        }
                    });
                }}
                onAssignSchedulesClick={() => {
                    console.log("Assign schedules clicked");
                    navigate(`/schedule`)
                }}
                isUpdateMode={isUpdateMode}
            />
            <RouteAddMap
                stops={allStopsData}
                selectedStops={routeData.selectedStops}
                onStopSelect={handleStopSelect}
                allStopsData={allStopsData}
                routeData={{
                    ...routeData,
                    onRouteDataChange: handleRouteDataChange
                }}
            />
            <RouteAddDetails
                routeData={routeData}
                onRouteDataChange={handleRouteDetailsChange}
                stops={allStopsData}
                vehicles={allVehiclesData}
                onVehicleSelect={handleVehicleSelection}
                onSubmit={handleSubmitRoute}
            />
        </>
    );
}